import React from 'react';
import { useHistory } from 'react-router-dom';
import { People } from '../hooks/api/useExperiences';
import { ROUTE_PATH } from '../routes/route-paths';
import { PhoneWhite26, CalendarIcon } from '../assets/images/svgs';
import useAuth from '../hooks/utils/useAuth';
import { AffiliationBanner, getAffiliationClientName } from './affiliation-banner';
import {
  hasSearchMatch,
  isExpandedMatchLayout,
  MatchStyleVariant,
  PeerListenerMatchHint,
  PeerListenerMatchRibbon,
} from './peer-listener-match-hint';

const BODY_HEIGHT_CLASS = 'h-[230px]';
const BODY_GROW_CLASS = 'min-h-[230px]';
const TILE_WIDTH_CLASS = 'w-[194px]';
const ROW_SIZE_CLASS = 'h-[254px] w-full min-w-0';
const BANNER_SLOT_CLASS = 'h-6 w-full shrink-0';
const AVATAR_SIZE_CLASS = 'h-[110px] w-[110px]';

interface PeerListenerTileProps {
  peer?: People;
  onScheduleClick?: (peer: People) => void;
  onTileClick?: (peer: People) => void;
  className?: string;
  forceAvailable?: boolean;
  isLoading?: boolean;
  hideScheduleButton?: boolean;
  matchStyle?: MatchStyleVariant;
  layout?: 'tile' | 'row';
}

export const PeerListenerTileSkeleton: React.FC<{ layout?: 'tile' | 'row' }> = ({ layout = 'tile' }) => {
  if (layout === 'row') {
    return (
      <div className={`flex overflow-hidden rounded-2xl bg-[#F4F5F8] ${ROW_SIZE_CLASS}`}>
        <div className={`flex h-full shrink-0 flex-col ${TILE_WIDTH_CLASS}`}>
          <div className={BANNER_SLOT_CLASS} aria-hidden />
          <div className={`flex flex-col items-center justify-between px-4 pb-4 pt-5 ${BODY_HEIGHT_CLASS}`}>
            <div className={`${AVATAR_SIZE_CLASS} animate-pulse rounded-full bg-gray-200`} />
            <div className="h-5 w-20 animate-pulse rounded bg-gray-200" />
            <div className="h-9 w-full animate-pulse rounded-full bg-gray-200" />
          </div>
        </div>
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden border-l border-white/70 px-4 pt-5">
          <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
          <div className="mt-2 h-16 w-full animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col overflow-hidden rounded-2xl bg-[#F4F5F8] ${TILE_WIDTH_CLASS}`}>
      <div className={BANNER_SLOT_CLASS} aria-hidden />
      <div className={`flex flex-col items-center justify-between px-4 pb-4 pt-5 ${BODY_HEIGHT_CLASS}`}>
        <div className={`${AVATAR_SIZE_CLASS} animate-pulse rounded-full bg-gray-200`} />
        <div className="h-5 w-20 animate-pulse rounded bg-gray-200" />
        <div className="h-9 w-full animate-pulse rounded-full bg-gray-200" />
      </div>
    </div>
  );
};

export const PeerListenerTile: React.FC<PeerListenerTileProps> = ({
  peer,
  onScheduleClick,
  onTileClick,
  className = '',
  isLoading = false,
  forceAvailable = false,
  hideScheduleButton = false,
  matchStyle = 'none',
  layout = 'tile',
}) => {
  const history = useHistory();
  const { user } = useAuth();

  if (isLoading || !peer) {
    return <PeerListenerTileSkeleton layout={layout} />;
  }

  // Schedule/Call chrome follows can_take_calls (viewer-effective), not is_listener.
  // Peers who cannot take calls should still appear in search without a schedule CTA (PS-314).
  const canTakeCalls = Boolean(peer.can_take_calls);
  const isAvailable = forceAvailable || peer.is_available;
  const affiliationName = getAffiliationClientName(peer, user?.client_id);
  const showMatch = matchStyle !== 'none' && hasSearchMatch(peer);
  const showRibbon = showMatch && matchStyle === 'ribbon';
  const useRowLayout = layout === 'row' && isExpandedMatchLayout(matchStyle);
  const showBodyHint = showMatch && matchStyle !== 'ribbon' && !useRowLayout;
  const bodyHeightClass = showBodyHint ? BODY_GROW_CLASS : BODY_HEIGHT_CLASS;

  const handleTileClick = () => {
    if (onTileClick) {
      onTileClick(peer);
    }
    history.push(`${ROUTE_PATH.PEER_DETAILS}?listenerId=${peer.listener_role_id}`);
  };

  const handleScheduleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onScheduleClick) {
      onScheduleClick(peer);
    } else {
      history.push(`${ROUTE_PATH.PEER_DETAILS}?listenerId=${peer.listener_role_id}&schedule=true`);
    }
  };

  const banner = affiliationName ? (
    <div className={BANNER_SLOT_CLASS}>
      <AffiliationBanner clientName={affiliationName} absolute={false} className="rounded-t-2xl" />
    </div>
  ) : showRibbon ? (
    <PeerListenerMatchRibbon match={peer} />
  ) : (
    <div className={BANNER_SLOT_CLASS} />
  );

  const compactBody = (
    <div className={`flex flex-col items-center justify-between gap-2 px-4 pb-4 pt-5 ${bodyHeightClass}`}>
      <div className={`${AVATAR_SIZE_CLASS} shrink-0 overflow-hidden rounded-full`}>
        <img
          src={peer.profile_photo_url_square}
          alt={peer.user.display_name}
          className="border-avatar-border-slate h-full w-full rounded-full border object-cover"
        />
      </div>

      <div className="flex min-h-6 w-full min-w-0 items-center justify-center gap-1.5">
        {canTakeCalls && (
          <span
            className={`h-2.5 w-2.5 shrink-0 rounded-full ${isAvailable ? 'bg-success' : 'bg-unavailable'}`}
            aria-hidden
          />
        )}
        <p className="font-albert-sans text-primary truncate text-center text-base font-semibold leading-5">
          {peer.user.display_name}
        </p>
      </div>

      {showBodyHint ? (
        <div className="flex w-full min-w-0 justify-center">
          <PeerListenerMatchHint match={peer} variant={matchStyle} />
        </div>
      ) : null}

      {!hideScheduleButton && canTakeCalls ? (
        <button
          onClick={handleScheduleClick}
          className={`flex h-9 w-full items-center justify-center gap-1.5 rounded-full px-4 py-2 transition-colors ${
            isAvailable ? 'bg-success hover:bg-success-hover' : 'bg-button-primary hover:bg-button-primary-hover'
          }`}
        >
          {isAvailable ? (
            <div className="flex h-4 w-4 shrink-0 items-center justify-center">
              <img src={PhoneWhite26} alt="Call now" className="h-4 w-4" />
            </div>
          ) : (
            <div className="flex h-4 w-4 shrink-0 items-center justify-center">
              <img src={CalendarIcon} alt="Schedule call" className="h-4 w-4" />
            </div>
          )}
          <p className="font-open-sans text-nowrap text-sm font-medium leading-5 text-white">
            {isAvailable ? 'Call now' : 'Schedule a Call'}
          </p>
        </button>
      ) : (
        <div className="h-9 w-full" aria-hidden />
      )}
    </div>
  );

  if (useRowLayout) {
    return (
      <div
        className={`relative flex cursor-pointer overflow-hidden rounded-2xl bg-[#F4F5F8] ${ROW_SIZE_CLASS} ${className}`}
        onClick={handleTileClick}
      >
        <div className={`flex h-full shrink-0 flex-col ${TILE_WIDTH_CLASS}`}>
          {banner}
          {compactBody}
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col items-stretch justify-start overflow-hidden border-l border-white/70 p-4">
          {showMatch ? (
            <div className="h-full min-h-0 w-full overflow-hidden">
              <PeerListenerMatchHint match={peer} variant={matchStyle} expanded />
            </div>
          ) : (
            <p className="text-sm text-gray-500">Related to your search</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative flex cursor-pointer flex-col overflow-hidden rounded-2xl bg-[#F4F5F8] ${TILE_WIDTH_CLASS} ${className}`}
      onClick={handleTileClick}
    >
      {banner}
      {affiliationName && showRibbon ? <PeerListenerMatchRibbon match={peer} /> : null}
      {compactBody}
    </div>
  );
};
